import {
  Button,
  formatUaePassTitle,
  Logo,
  TextInput,
  Typography,
} from "@/components/atoms";
import { XIcon } from "@/components/atoms/icons";
import { OTPInput } from "@/components/molecules/OTPInput";
import { Toast } from "@/components/molecules/Toast";
import {
  uaePassLoginButtonFallback,
  uaePassLoginButtonFallbackAspect,
} from "@/components/molecules/uaePassLoginButtonFallback";
import {
  jwtHasUaepassClaim,
  readConvertedLeadFlag,
  shouldEnterAdhaJourney,
} from "@/core/constants/adhaJourney";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useAuth } from "@/core/contexts/AuthContext";
import {
  useEmiratesIdOtpResendSeconds,
  useFocusVisible,
  useTranslation,
  useUIColors,
  useWebViewport,
} from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import {
  fetchUaePassLoginButtonImage,
  type UaePassLoginButtonImage,
} from "@/core/services/contentfulClient";
import {
  formatOtpResendCountdown,
  splitOtpResendTimerTemplate,
} from "@/core/utils/otpResendTimerDisplay";
import { VIEWPORT_ZOOM_EPSILON } from "@/core/utils/viewportMetrics";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import {
  trackCatalogEvent,
  trackEmiratesIdEmailOtpVerifyClick,
  trackEmiratesIdEmailOtpVerifyError,
  trackEmiratesIdEmailOtpVerifySuccess,
  trackEmiratesIdLoginStartClick,
  trackEmiratesIdLoginStepView,
  trackEmiratesIdMobileOtpVerifyClick,
  trackEmiratesIdMobileOtpVerifyError,
  trackEmiratesIdMobileOtpVerifySuccess,
  trackEmiratesIdOtpResendClick,
  trackEmiratesIdOtpResendError,
  trackEmiratesIdRaiseSupportClick,
  trackEmiratesIdSubmitClick,
  trackEmiratesIdSubmitError,
} from '@/core/services/trackDynatraceCatalog';
import { EMIRATES_ID_AUTH_MESSAGES } from "@/modules/auth/constants/emiratesIdAuth";
import { useAdhaLogin } from "@/modules/auth/hooks/useAdhaLogin";
import { ADHA_LOGIN_VERIFICATION_PROCESS_TYPE, authService, readSendOtpJwtToken } from "@/modules/auth/services/authService";
import { hydrateNormalCustomerFromProfile } from "@/modules/auth/services/hydrateNormalCustomerFromProfile";
import { clearAdhaDeepLink } from "@/modules/properties-web/utils/adhaDeepLinkRedirect";
import {
  clearSuspendedAuthFlow,
  consumeSuspendedAuthFlow,
  suspendAuthFlow,
} from "@/modules/auth/utils/authFlowState";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type PressableStateCallbackType,
  type ViewStyle,
} from "react-native";

export enum LoginFlowStep {
  LOGIN = "LOGIN",
  EMIRATES_ID = "EMIRATES_ID",
  MOBILE_OTP = "MOBILE_OTP",
  EMAIL_OTP = "EMAIL_OTP",
}

export interface LoginDialogProps {
  visible: boolean;
  onClose: () => void;
  onLogin?: (email: string, password: string) => void | Promise<void>;
  onForgotPassword?: () => void;
  /** ADHA / UAE Pass entry (Basic Auth profile + units catalogue) */
  onContinueWithUAEPass?: () => void | Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  /**
   * Contentful-driven flags that control which alternative login methods are
   * shown. Defaults to `true` (visible) when the config has not loaded yet.
   */
  isUAEpassLogin?: boolean;
  isEmiratesIdLogin?: boolean;
  /** Deep-link / marketing: open on Emirates ID input instead of email login. */
  initialStep?: LoginFlowStep;
  /** User tapped “Login with Emirates ID” inside the dialog. */
  onEmiratesIdPress?: () => void;
}

/** Matches the rendered size of the approved UAE PASS login button on the Bloom website (324×64, official 506×100 artwork). */
const UAE_PASS_BUTTON_WIDTH = 324;

function compactStyle<T>(isCompact: boolean, style: T): T | false {
  return isCompact ? style : false;
}

function resolveLoginDialogStartStep(
  initialStep: LoginFlowStep | undefined,
  isEmiratesIdLogin: boolean,
): LoginFlowStep {
  if (initialStep === LoginFlowStep.EMIRATES_ID && isEmiratesIdLogin) {
    return LoginFlowStep.EMIRATES_ID;
  }
  return LoginFlowStep.LOGIN;
}

function renderErrorMessage(
  isSmallViewport: boolean,
  externalError?: string | null,
): React.ReactNode {
  if (isSmallViewport) {
    return (
      <View style={styles.errorSlotCompact}>
        {externalError ? (
          <Typography
            variant="subhead"
            color="error"
            style={styles.errorTextCompact}
          >
            {externalError}
          </Typography>
        ) : null}
      </View>
    );
  }

  if (!externalError) {
    return null;
  }

  return (
    <Typography variant="body" color="error" style={styles.errorText}>
      {externalError}
    </Typography>
  );
}

const LoginFormStep = ({
  isSmallViewport,
  reduceForgotToLoginGap = false,
  email,
  setEmail,
  password,
  setPassword,
  emailError,
  passwordError,
  externalError,
  handleForgotPasswordPress,
  isLoading,
  handleSignIn,
  handleUAEPassPress,
  onContinueWithUAEPass,
  setStep,
  onEmiratesIdPress,
  isUAEpassLogin = true,
  isEmiratesIdLogin = true,
}: any) => {
  const { t } = useTranslation();
  const colors = useUIColors();
  const { currentLanguage } = useI18nContext();
  const uaePassFocus = useFocusVisible();
  const [uaePassButtonImage, setUaePassButtonImage] =
    useState<UaePassLoginButtonImage | null>(null);
  const [uaePassButtonResolved, setUaePassButtonResolved] = useState(false);
  const [uaePassFailedUrl, setUaePassFailedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isUAEpassLogin) {
      setUaePassButtonImage(null);
      setUaePassButtonResolved(true);
      return undefined;
    }
    let cancelled = false;
    setUaePassButtonResolved(false);
    setUaePassFailedUrl(null);
    const locale = currentLanguage === "ar" ? "ar" : "en";
    void fetchUaePassLoginButtonImage(locale)
      .catch(() => null)
      .then((image) => {
        if (cancelled) return;
        setUaePassButtonImage(image);
        setUaePassButtonResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentLanguage, isUAEpassLogin]);

  const isUaePassDisabled = isLoading || !onContinueWithUAEPass;

  const renderUaePassButton = () => {
    const remoteImage =
      uaePassButtonImage && uaePassButtonImage.url !== uaePassFailedUrl
        ? uaePassButtonImage
        : null;

    const fallbackAspect = uaePassLoginButtonFallbackAspect(currentLanguage);

    if (!remoteImage && !uaePassButtonResolved) {
      return (
        <View
          style={[styles.uaePassImagePlaceholder, { aspectRatio: fallbackAspect }]}
        />
      );
    }

    return (
      <Pressable
        onPress={handleUAEPassPress}
        disabled={isUaePassDisabled}
        accessibilityRole="button"
        accessibilityLabel={formatUaePassTitle(t("auth.loginWithUAEPass"))}
        accessibilityState={{ disabled: isUaePassDisabled }}
        onFocus={uaePassFocus.onFocus}
        onBlur={uaePassFocus.onBlur}
        style={({ pressed }: PressableStateCallbackType) => [
          styles.uaePassImageButton,
          uaePassFocus.isFocusVisible &&
            !isUaePassDisabled &&
            styles.uaePassImageButtonFocused,
          pressed && !isUaePassDisabled && styles.uaePassImageButtonPressed,
          isUaePassDisabled && styles.uaePassImageButtonDisabled,
        ]}
      >
        <Image
          source={
            remoteImage
              ? { uri: remoteImage.url }
              : uaePassLoginButtonFallback(currentLanguage)
          }
          style={{
            width: "100%",
            aspectRatio: remoteImage
              ? remoteImage.width / remoteImage.height
              : fallbackAspect,
          }}
          contentFit="contain"
          accessibilityIgnoresInvertColors
          {...(remoteImage
            ? { onError: () => setUaePassFailedUrl(remoteImage.url) }
            : {})}
        />
      </Pressable>
    );
  };

  return (
    <View
      style={[
        styles.column,
        compactStyle(isSmallViewport, styles.columnCompact),
      ]}
    >
      <Typography
        variant={isSmallViewport ? "title2" : "title1"}
        color="primary"
        style={styles.columnTitle}
      >
        {t("auth.login")}
      </Typography>

      <View
        style={[
          styles.formSection,
          compactStyle(isSmallViewport, styles.formSectionCompact),
        ]}
      >
        <View
          style={[
            styles.inputGroup,
            compactStyle(isSmallViewport, styles.inputGroupCompact),
          ]}
        >
          <TextInput
            label={t("auth.emailId")}
            placeholder={t("auth.enterEmailId")}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            error={emailError}
            required
          />
        </View>

        <View
          style={[
            styles.inputGroup,
            compactStyle(isSmallViewport, styles.inputGroupCompact),
          ]}
        >
          <TextInput
            label={t("auth.password")}
            placeholder={t("auth.enterPassword")}
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            error={passwordError}
            required
          />
        </View>

        {renderErrorMessage(isSmallViewport, externalError)}

        <Button
          title={t("auth.forgotPassword")}
          onPress={handleForgotPasswordPress}
          variant="text"
          fullWidth
          style={[
            compactStyle(
              isSmallViewport,
              externalError
                ? styles.forgotPasswordWithErrorCompact
                : styles.forgotPasswordNoErrorCompact,
            ),
            // 125%+ desktop: ~2–4px above Forgot (formSection gap is 32).
            compactStyle(
              reduceForgotToLoginGap && !isSmallViewport,
              externalError
                ? styles.forgotAboveGapZoom125WithError
                : styles.forgotAboveGapZoom125NoError,
            ),
          ]}
          disabled={isLoading}
        />

        <Button
          title={
            isLoading
              ? t("common.loading") || "Loading..."
              : t("auth.login")
          }
          variant="primary"
          onPress={handleSignIn}
          fullWidth
          style={[
            styles.button,
            compactStyle(isSmallViewport, styles.compactButton),
            compactStyle(isSmallViewport, styles.loginButtonCompact),
            compactStyle(
              reduceForgotToLoginGap,
              styles.forgotToLoginGapZoom125,
            ),
          ]}
          disabled={isLoading}
        />

        {(isUAEpassLogin || isEmiratesIdLogin) && (
          <>
            <View
              style={[
                styles.dividerContainer,
                compactStyle(
                  isSmallViewport,
                  styles.dividerContainerCompact,
                ),
              ]}
            >
              <View
                style={[
                  styles.dividerLine,
                  { backgroundColor: colors.backgroundLevel4 },
                ]}
              />
              <Typography
                variant="caption"
                color="tertiary"
                style={styles.orText}
              >
                {t("welcome.or")}
              </Typography>
              <View
                style={[
                  styles.dividerLine,
                  { backgroundColor: colors.backgroundLevel4 },
                ]}
              />
            </View>

            <View style={{ gap: 8, width: '100%', alignItems: 'center' }}>
              {isUAEpassLogin && (
                <View
                  style={[
                    styles.uaePassButtonContainer,
                    compactStyle(
                      isSmallViewport,
                      styles.uaePassButtonContainerCompact,
                    ),
                  ]}
                >
                  {renderUaePassButton()}
                </View>
              )}

              {isEmiratesIdLogin && (
                <View
                  style={[
                    styles.uaePassButtonContainer,
                    compactStyle(
                      isSmallViewport,
                      styles.uaePassButtonContainerCompact,
                    ),
                  ]}
                >
                  <Button
                    title={t("auth.loginWithEmiratesIDButton")}
                    variant="primary"
                    fullWidth
                    style={[
                      styles.button,
                      compactStyle(isSmallViewport, styles.compactButton),
                      compactStyle(isSmallViewport, styles.uaePassButtonCompact),
                    ]}
                    onPress={() => {
                      trackEmiratesIdLoginStartClick();
                      setStep(LoginFlowStep.EMIRATES_ID);
                      onEmiratesIdPress?.();
                    }}
                    disabled={isLoading}
                  />
                </View>
              )}
            </View>
          </>
        )}
      </View>
    </View>
  );
};

export const LoginDialog: React.FC<Readonly<LoginDialogProps>> = ({
  visible,
  onClose,
  onLogin,
  onForgotPassword,
  onContinueWithUAEPass,
  isLoading = false,
  error: externalError,
  isUAEpassLogin = true,
  isEmiratesIdLogin = true,
  initialStep,
  onEmiratesIdPress,
}) => {
  const colors = useUIColors();
  const emiratesIdOtpResendSeconds = useEmiratesIdOtpResendSeconds();
  const { t } = useTranslation();
  const router = useRouter();
  const {
    breakpointWidth: viewportWidth,
    breakpointHeight: viewportHeight,
    browserZoom,
  } = useWebViewport();

  const isSmallViewport = viewportWidth < 600;
  const isTallSmallViewport = isSmallViewport && viewportHeight >= 800;
  const isFullScreenSmallViewport = isSmallViewport && !isTallSmallViewport;
  /** 125%+: tighten Forgot ↔ field/error and Forgot → Login gaps. */
  const reduceForgotToLoginGap =
    browserZoom >= 1.25 - VIEWPORT_ZOOM_EPSILON;
  /**
   * Cap only (zoom-corrected height). Dialog hugs content while it fits;
   * inner ScrollView scrolls only once content exceeds this ceiling.
   * Small short phones (e.g. iPhone SE): true full-bleed — no edge inset.
   */
  const DIALOG_EDGE_INSET = 48;
  const dialogMaxHeight = isFullScreenSmallViewport
    ? Math.max(240, viewportHeight)
    : Math.max(240, viewportHeight - DIALOG_EDGE_INSET);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [step, setStep] = useState<LoginFlowStep>(() =>
    resolveLoginDialogStartStep(initialStep, isEmiratesIdLogin),
  );
  const [emiratesId, setEmiratesId] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(emiratesIdOtpResendSeconds);

  const [tempJwtToken, setTempJwtToken] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [otpJwtToken, setOtpJwtToken] = useState("");
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const { login: authLogin } = useAuth();
  const { completeAdhaLoginWithToken } = useAdhaLogin();
  const dialogViewTrackedRef = useRef(false);
  const submitErrorTrackedRef = useRef<string | null>(null);
  const emiratesStepViewTrackedRef = useRef<Record<string, boolean>>({});

  const finishEmiratesIdSession = useCallback(
    async (
      verifyRes: unknown,
      finalToken: string,
      otpChannel: "mobile" | "email",
    ) => {
      if (otpChannel === "mobile") {
        trackEmiratesIdMobileOtpVerifySuccess();
      } else {
        trackEmiratesIdEmailOtpVerifySuccess();
      }

      const enterAdha = shouldEnterAdhaJourney({
        convertedLead: readConvertedLeadFlag(verifyRes),
        hasUaepassClaim: jwtHasUaepassClaim(finalToken),
      });

      if (enterAdha) {
        await completeAdhaLoginWithToken(finalToken);
        clearSuspendedAuthFlow();
        onClose();
        return;
      }

      // Converted lead (or non-ADHA Emirates ID): same profile hydrate as email login
      await hydrateNormalCustomerFromProfile(finalToken, "emirates_id");
      clearAdhaDeepLink();
      authLogin();
      clearSuspendedAuthFlow();
      onClose();
      router.replace("/(auth)/(home)" as never);
    },
    [authLogin, completeAdhaLoginWithToken, onClose, router],
  );

  useEffect(() => {
    // Only initialize when the dialog opens. Do NOT re-run when initialStep
    // changes mid-session (e.g. parent re-render after Emirates ID click) —
    // that was resetting OTP back to the Emirates ID form and blocking redirect.
    if (!visible) {
      return;
    }

    const state = consumeSuspendedAuthFlow();
    if (state?.flowType === 'web_login') {
      const restoredStep = Object.values(LoginFlowStep).includes(
        state.webStep as LoginFlowStep,
      )
        ? (state.webStep as LoginFlowStep)
        : LoginFlowStep.LOGIN;
      setStep(restoredStep);
      setEmail("");
      setPassword("");
      setEmiratesId(state.webEmiratesId || "");
      setMobileOtp(state.webMobileOtp || "");
      setEmailOtp(state.webEmailOtp || "");
      setTempPhoneNumber(state.webTempPhoneNumber || "");
      setTempEmail(state.webTempEmail || "");
      setTempJwtToken(state.webTempJwtToken || "");
      setOtpJwtToken(state.webOtpJwtToken || "");

      if (state.webResendTimer !== undefined && state.suspendedAt) {
        const elapsedSecs = Math.floor((Date.now() - state.suspendedAt) / 1000);
        setResendTimer(Math.max(0, state.webResendTimer - elapsedSecs));
      } else {
        setResendTimer(emiratesIdOtpResendSeconds);
      }
    } else {
      setStep(resolveLoginDialogStartStep(initialStep, isEmiratesIdLogin));
      setEmail("");
      setPassword("");
      setEmiratesId("");
      setMobileOtp("");
      setEmailOtp("");
      setTempPhoneNumber("");
      setTempEmail("");
      setTempJwtToken("");
      setOtpJwtToken("");
      setResendTimer(emiratesIdOtpResendSeconds);
    }
    setLocalError("");
    setEmailError("");
    setPasswordError("");
    setToastMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init only on open
  }, [visible, emiratesIdOtpResendSeconds]);

  useEffect(() => {
    if (!visible) {
      dialogViewTrackedRef.current = false;
      submitErrorTrackedRef.current = null;
      return;
    }
    if (dialogViewTrackedRef.current) {
      return;
    }
    dialogViewTrackedRef.current = true;
    trackCatalogEvent({
      screen: DT_SCREEN.WELCOME,
      actionName: "login_dialog_viewed",
      eventType: "view",
    });
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      emiratesStepViewTrackedRef.current = {};
      return;
    }
    if (
      step !== LoginFlowStep.EMIRATES_ID &&
      step !== LoginFlowStep.MOBILE_OTP &&
      step !== LoginFlowStep.EMAIL_OTP
    ) {
      return;
    }
    if (emiratesStepViewTrackedRef.current[step]) {
      return;
    }
    emiratesStepViewTrackedRef.current[step] = true;
    let stepKey: "EMIRATES_ID" | "MOBILE_OTP" | "EMAIL_OTP" = "EMAIL_OTP";
    if (step === LoginFlowStep.EMIRATES_ID) {
      stepKey = "EMIRATES_ID";
    } else if (step === LoginFlowStep.MOBILE_OTP) {
      stepKey = "MOBILE_OTP";
    }
    trackEmiratesIdLoginStepView(stepKey);
  }, [visible, step]);

  useEffect(() => {
    if (!visible || !externalError) {
      return;
    }
    if (submitErrorTrackedRef.current === externalError) {
      return;
    }
    submitErrorTrackedRef.current = externalError;
    trackCatalogEvent({
      screen: DT_SCREEN.WELCOME,
      actionName: "login_dialog_submit_error",
      eventType: "error",
      properties: { p_failedMessage: externalError },
    });
  }, [visible, externalError]);

  const handleCloseAction = () => {
    trackCatalogEvent({
      screen: DT_SCREEN.WELCOME,
      actionName: "login_dialog_close",
      eventType: "action",
    });
    clearSuspendedAuthFlow();
    onClose();
  };

  const handleRaiseSupportTicket = () => {
    trackEmiratesIdRaiseSupportClick(
      step === LoginFlowStep.EMAIL_OTP ? "EMAIL_OTP" : "MOBILE_OTP",
    );
    suspendAuthFlow({
      flowType: 'web_login',
      webStep: step,
      webEmiratesId: emiratesId,
      webMobileOtp: mobileOtp,
      webEmailOtp: emailOtp,
      webTempPhoneNumber: tempPhoneNumber,
      webTempEmail: tempEmail,
      webTempJwtToken: tempJwtToken,
      webOtpJwtToken: otpJwtToken,
      webResendTimer: resendTimer,
    });
    onClose();
    router.push({ pathname: '/service-request-create', params: { authFlow: 'true' } } as any);
  };

  useEffect(() => {
    if ((step === LoginFlowStep.MOBILE_OTP || step === LoginFlowStep.EMAIL_OTP) && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendTimer, step]);

  const validateEmail = (emailValue: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleEmiratesIdSubmit = async () => {
    if (localIsLoading || emiratesId.length !== 15) return;
    setLocalIsLoading(true);
    setLocalError("");
    trackEmiratesIdSubmitClick();
    try {
      // 1. Sign in with Emirates ID
      const digits = emiratesId;
      const formattedEmiratesId = `${digits.substring(0, 3)}-${digits.substring(3, 7)}-${digits.substring(7, 14)}-${digits.substring(14, 15)}`;
      const signInRes = await authService.loginWithEmiratesId(formattedEmiratesId);
      if (signInRes.status !== 'success') {
        throw new Error((signInRes as any).details?.message || signInRes.message || EMIRATES_ID_AUTH_MESSAGES.SIGN_IN_FAILED);
      }

      const token = signInRes.data?.jwtToken || (signInRes as any).details?.jwtToken || (signInRes as any).jwtToken;
      const emailVal = signInRes.data?.email || (signInRes as any).details?.email || (signInRes as any).email || "";
      const phoneVal = signInRes.data?.phoneNumber || (signInRes as any).details?.phoneNumber || (signInRes as any).phoneNumber || "";

      setTempJwtToken(token);
      setTempEmail(emailVal);
      setTempPhoneNumber(phoneVal);

      // 2. Automatically send SMS OTP
      const otpRes = await authService.sendEmiratesIdOtp(token, "SMS", { mobileNumber: phoneVal });
      if (otpRes.status !== 'success') {
        throw new Error((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_SEND_FAILED);
      }
      const sendOtpToken = readSendOtpJwtToken(otpRes);
      if (!sendOtpToken) {
        throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_OTP_TOKEN);
      }
      setOtpJwtToken(sendOtpToken);
      setToastMessage((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);

      setStep(LoginFlowStep.MOBILE_OTP);
      setResendTimer(emiratesIdOtpResendSeconds);
    } catch (err: any) {
      const apiErrMsg = err.response?.details?.message || err.response?.data?.details?.message || err.response?.data?.message || err.response?.message || err.details?.message;
      const message = apiErrMsg || err.message || EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR;
      setLocalError(message);
      trackEmiratesIdSubmitError(message);
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleMobileOtpResend = async () => {
    if (localIsLoading || !tempJwtToken || resendTimer > 0) return;
    setLocalIsLoading(true);
    setLocalError("");
    trackEmiratesIdOtpResendClick("mobile");
    try {
      const otpRes = await authService.sendEmiratesIdOtp(tempJwtToken, "SMS", { mobileNumber: tempPhoneNumber });
      if (otpRes.status !== 'success') {
        throw new Error((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_RESEND_FAILED);
      }
      const sendOtpToken = readSendOtpJwtToken(otpRes);
      if (!sendOtpToken) throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_OTP_TOKEN);
      setOtpJwtToken(sendOtpToken);
      setToastMessage((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);
      setResendTimer(emiratesIdOtpResendSeconds);
    } catch (err: any) {
      const apiErrMsg = err.response?.details?.message || err.response?.data?.details?.message || err.response?.data?.message || err.response?.message || err.details?.message;
      const message = apiErrMsg || err.message || EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR;
      setLocalError(message);
      trackEmiratesIdOtpResendError("mobile", message);
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleMobileOtpVerify = async () => {
    if (localIsLoading || mobileOtp.length !== 4) return;
    trackEmiratesIdMobileOtpVerifyClick();
    setLocalIsLoading(true);
    setLocalError("");
    try {
      // Verify Mobile OTP
      const verifyRes = await authService.verifyEmiratesIdOtp(
        otpJwtToken,
        mobileOtp,
        ADHA_LOGIN_VERIFICATION_PROCESS_TYPE,
      );
      if (verifyRes.status !== 'success') {
        throw new Error((verifyRes as any).details?.message || verifyRes.message || EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_VERIFY_FAILED);
      }

      /* 
      // Commenting out email verification step
      // Auto trigger Email OTP
      const otpRes = await authService.sendEmiratesIdOtp(tempJwtToken, "Email", { email: tempEmail });
      if (otpRes.status !== 'success') {
        throw new Error((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_SEND_FAILED);
      }
      const txId = otpRes.data?.transactionId || (otpRes as any).details?.transactionId || (otpRes as any).transactionId;
      if (!txId) throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_TRANSACTION_ID);
      setTransactionId(txId);
      setToastMessage((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);

      setStep(LoginFlowStep.EMAIL_OTP);
      setResendTimer(emiratesIdOtpResendSeconds);
      */

      // Final Login
      const finalToken = verifyRes.data?.jwtToken || (verifyRes as any).details?.jwtToken || (verifyRes as any).jwtToken;
      await finishEmiratesIdSession(verifyRes, finalToken, "mobile");
    } catch (err: any) {
      const apiErrMsg = err.response?.details?.message || err.response?.data?.details?.message || err.response?.data?.message || err.response?.message || err.details?.message;
      const message = apiErrMsg || err.message || EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR;
      setLocalError(message);
      trackEmiratesIdMobileOtpVerifyError(message);
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleEmailOtpResend = async () => {
    if (localIsLoading || !tempJwtToken || resendTimer > 0) return;
    setLocalIsLoading(true);
    setLocalError("");
    trackEmiratesIdOtpResendClick("email");
    try {
      const otpRes = await authService.sendEmiratesIdOtp(tempJwtToken, "Email", { email: tempEmail });
      if (otpRes.status !== 'success') {
        throw new Error((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_RESEND_FAILED);
      }
      const sendOtpToken = readSendOtpJwtToken(otpRes);
      if (!sendOtpToken) throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_OTP_TOKEN);
      setOtpJwtToken(sendOtpToken);
      setToastMessage((otpRes as any).details?.message || otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);
      setResendTimer(emiratesIdOtpResendSeconds);
    } catch (err: any) {
      const apiErrMsg = err.response?.details?.message || err.response?.data?.details?.message || err.response?.data?.message || err.response?.message || err.details?.message;
      const message = apiErrMsg || err.message || EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR;
      setLocalError(message);
      trackEmiratesIdOtpResendError("email", message);
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleEmailOtpVerify = async () => {
    if (localIsLoading || emailOtp.length !== 4) return;
    trackEmiratesIdEmailOtpVerifyClick();
    setLocalIsLoading(true);
    setLocalError("");
    try {
      const verifyRes = await authService.verifyEmiratesIdOtp(
        otpJwtToken,
        emailOtp,
        ADHA_LOGIN_VERIFICATION_PROCESS_TYPE,
      );
      if (verifyRes.status !== 'success') {
        throw new Error((verifyRes as any).details?.message || verifyRes.message || EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_VERIFY_FAILED);
      }

      // Final Login
      const finalToken = verifyRes.data?.jwtToken || (verifyRes as any).details?.jwtToken || (verifyRes as any).jwtToken;
      await finishEmiratesIdSession(verifyRes, finalToken, "email");
    } catch (err: any) {
      const apiErrMsg = err.response?.details?.message || err.response?.data?.details?.message || err.response?.data?.message || err.response?.message || err.details?.message;
      const message = apiErrMsg || err.message || EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR;
      setLocalError(message);
      trackEmiratesIdEmailOtpVerifyError(message);
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (isLoading) return;

    setEmailError("");
    setPasswordError("");

    let hasError = false;

    if (!email.trim()) {
      setEmailError(t("auth.emailRequired"));
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError(t("auth.invalidEmail"));
      hasError = true;
    }

    if (!password) {
      setPasswordError(t("auth.passwordRequired"));
      hasError = true;
    }

    if (!hasError && onLogin) {
      trackCatalogEvent({
        screen: DT_SCREEN.WELCOME,
        actionName: "login_dialog_submit",
        eventType: "action",
      });
      try {
        await onLogin(email, password);
      } catch {
        // Error is handled by parent and passed via error prop
      }
    }
  };

  const handleForgotPasswordPress = () => {
    onClose();
    if (onForgotPassword) {
      onForgotPassword();
    } else {
      router.push("/forgot-password");
    }
  };

  const handleUAEPassPress = async () => {
    if (isLoading || !onContinueWithUAEPass) return;
    trackCatalogEvent({
      screen: DT_SCREEN.WELCOME,
      actionName: "login_dialog_uae_pass_start",
      eventType: "action",
    });
    await onContinueWithUAEPass();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalRoot}>
        <Pressable
          style={[
            styles.overlay,
            isFullScreenSmallViewport
              ? styles.overlayFullScreen
              : undefined,
          ]}
          onPress={handleCloseAction}
          disabled={isLoading}
        >
          <Pressable
            style={[
              styles.container,
              isFullScreenSmallViewport
                ? styles.containerFullScreen
                : undefined,
              {
                backgroundColor: colors.white,
                maxHeight: dialogMaxHeight,
                // Full-bleed phone: stretch to the full viewport (no inset gaps).
                // Otherwise omit height so the card hugs content under the cap.
                ...(isFullScreenSmallViewport
                  ? { height: dialogMaxHeight, maxWidth: "100%" as const }
                  : null),
              },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <Pressable
              style={[styles.closeButton, { right: 32, left: undefined }]}
              onPress={(e) => {
                e.stopPropagation();
                handleCloseAction();
              }}
              disabled={isLoading}
            >
              <XIcon size={24} color={colors.fontPrimary} />
            </Pressable>

            <ScrollView
              style={[
                styles.dialogBodyScroll,
                isFullScreenSmallViewport
                  ? styles.dialogBodyScrollFullScreen
                  : null,
              ]}
              contentContainerStyle={styles.dialogBodyContent}
              keyboardShouldPersistTaps="handled"
              bounces={false}
              showsVerticalScrollIndicator={false}
              showsHorizontalScrollIndicator={false}
              {...(Platform.OS === "web"
                ? ({
                    className: isFullScreenSmallViewport
                      ? "login-dialog-scroll login-dialog-scroll-fullscreen"
                      : "login-dialog-scroll",
                  } as object)
                : null)}
            >
              <View
                style={[
                  styles.logoContainer,
                  compactStyle(isSmallViewport, styles.logoContainerCompact),
                ]}
              >
                <Logo color="bloomRed" size="large" />
              </View>

              {step === LoginFlowStep.LOGIN && (
                <View
                  style={[
                    styles.titleSection,
                    compactStyle(
                      isSmallViewport,
                      styles.titleSectionCompact,
                    ),
                  ]}
                >
                  <Typography
                    variant="title2"
                    color="primary"
                    style={styles.welcomeText}
                  >
                    {t("welcome.welcomeTo")}
                  </Typography>
                  <Typography
                    variant={isSmallViewport ? "title1" : "largeTitle"}
                    color="primary"
                    style={styles.appName}
                  >
                    {t("welcome.appName")}
                  </Typography>
                  <View style={styles.subtitleContainer}>
                    <Typography
                      variant={isSmallViewport ? "subhead" : "bodyLarge"}
                      color="primary"
                      style={styles.subtitle}
                      numberOfLines={1}
                      adjustsFontSizeToFit
                      minimumFontScale={0.8}
                    >
                      {t("welcome.subtitle1")}
                    </Typography>
                  </View>
                </View>
              )}

              <View style={styles.mainContent}>
                {(() => {
                  switch (step) {
                    case LoginFlowStep.LOGIN:
                      return (
                        <LoginFormStep
                          isSmallViewport={isSmallViewport}
                          reduceForgotToLoginGap={reduceForgotToLoginGap}
                          email={email}
                          setEmail={setEmail}
                          password={password}
                          setPassword={setPassword}
                          emailError={emailError}
                          passwordError={passwordError}
                          externalError={externalError}
                          handleForgotPasswordPress={
                            handleForgotPasswordPress
                          }
                          isLoading={isLoading}
                          handleSignIn={handleSignIn}
                          handleUAEPassPress={handleUAEPassPress}
                          onContinueWithUAEPass={onContinueWithUAEPass}
                          setStep={setStep}
                          onEmiratesIdPress={onEmiratesIdPress}
                          isUAEpassLogin={isUAEpassLogin}
                          isEmiratesIdLogin={isEmiratesIdLogin}
                        />
                      );
                    case LoginFlowStep.EMIRATES_ID:
                      return (
                        <EmiratesIdInputStep
                          emiratesId={emiratesId}
                          setEmiratesId={(val) => {
                            setEmiratesId(val);
                            if (localError) setLocalError("");
                          }}
                          onContinue={handleEmiratesIdSubmit}
                          isSmallViewport={isSmallViewport}
                          error={localError}
                        />
                      );
                    case LoginFlowStep.MOBILE_OTP:
                      return (
                        <OTPVerificationStep
                          key="mobile-otp"
                          contactInfo={tempPhoneNumber || "Phone"}
                          otpValue={mobileOtp}
                          setOtpValue={(val) => {
                            setMobileOtp(val);
                            if (localError) setLocalError("");
                          }}
                          resendTimer={resendTimer}
                          onVerify={handleMobileOtpVerify}
                          onResend={handleMobileOtpResend}
                          isSmallViewport={isSmallViewport}
                          type="mobile"
                          onClose={onClose}
                          onSupportTicketPress={handleRaiseSupportTicket}
                          error={localError}
                          isLoading={localIsLoading}
                        />
                      );
                    case LoginFlowStep.EMAIL_OTP:
                      return (
                        <OTPVerificationStep
                          key="email-otp"
                          contactInfo={tempEmail || "Email"}
                          otpValue={emailOtp}
                          setOtpValue={(val) => {
                            setEmailOtp(val);
                            if (localError) setLocalError("");
                          }}
                          resendTimer={resendTimer}
                          onVerify={handleEmailOtpVerify}
                          onResend={handleEmailOtpResend}
                          isSmallViewport={isSmallViewport}
                          type="email"
                          onClose={onClose}
                          onSupportTicketPress={handleRaiseSupportTicket}
                          error={localError}
                          isLoading={localIsLoading}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>

        <Toast
          visible={!!toastMessage}
          message={toastMessage}
          onHide={() => setToastMessage("")}
          position="top-right"
        />

        {isLoading || localIsLoading ? (
          <View style={styles.loaderOverlay} pointerEvents="auto">
            <View style={styles.loaderContainer}>
              <Image
                source={require("@assets/images/gif/loader.gif")}
                style={styles.loaderImage}
                contentFit="contain"
              />
            </View>
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalRoot: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: UI_PRIMITIVES.overlayBackdrop,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  overlayFullScreen: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  container: {
    borderRadius: 24,
    width: "100%",
    maxWidth: 523,
    position: "relative",
    overflow: "hidden",
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
          flexDirection: "column",
        } as object)
      : null),
  },
  containerFullScreen: {
    width: "100%",
    maxWidth: "100%",
    borderRadius: 0,
  },
  /**
   * Hug content under maxHeight. When content exceeds the viewport cap,
   * flex-shrink lets this ScrollView scroll inside the dialog.
   */
  dialogBodyScroll: {
    width: "100%",
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        } as object)
      : null),
  },
  /** Fill the full-bleed phone card so content can scroll within the viewport. */
  dialogBodyScrollFullScreen: {
    flexGrow: 1,
    flex: 1,
    height: "100%",
  },
  dialogBodyContent: {
    flexGrow: 0,
    paddingTop: 48,
    paddingBottom: 64,
    paddingHorizontal: 48,
  },
  closeButton: {
    position: "absolute",
    top: 32,
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  logoContainer: {
    alignItems: "center",
    marginBottom: 24,
  },
  logoContainerCompact: {
    marginBottom: 12,
  },
  titleSection: {
    alignItems: "center",
    marginBottom: 40,
    gap: 16,
  },
  titleSectionCompact: {
    marginBottom: 20,
    gap: 8,
  },
  welcomeText: {
    textAlign: "center",
  },
  appName: {
    textAlign: "center",
  },
  subtitle: {
    textAlign: "center",
  },
  subtitleContainer: {
    gap: 8,
  },
  mainContent: {
    width: "100%",
    alignSelf: "center",
  },
  column: {
    width: "100%",
    gap: 20,
  },
  columnCompact: {
    gap: 12,
  },
  columnTitle: {
    textAlign: "center",
  },
  formSection: {
    gap: 32,
  },
  formSectionCompact: {
    gap: 12,
  },
  inputGroup: {
    gap: 8,
  },
  inputGroupCompact: {
    gap: 4,
  },
  button: {
    height: 48,
  },
  compactButton: {
    height: 44,
  },
  loginButtonCompact: {
    marginTop: -8,
  },
  /** 125%+: pull Login closer to Forgot (formSection gap is 32). */
  forgotToLoginGapZoom125: {
    marginTop: -16,
  },
  /** 125%+ desktop: password/error → Forgot ≈ 4px (32 − 28). */
  forgotAboveGapZoom125NoError: {
    marginTop: -28,
  },
  /** 125%+ desktop: error marginBottom 8 + gap 32 → pull to ≈ 4px. */
  forgotAboveGapZoom125WithError: {
    marginTop: -36,
  },
  dividerContainerCompact: {
    gap: 4,
    marginVertical: 4,
  },
  uaePassButtonContainer: {
    alignItems: "center",
    width: "100%",
  },
  uaePassButtonContainerCompact: {
    marginTop: 4,
  },
  uaePassButton: {
    width: 300,
    height: 45,
    alignSelf: "center",
  },
  uaePassButtonCompact: {
    width: "100%",
  },
  uaePassImageButton: {
    width: UAE_PASS_BUTTON_WIDTH,
    maxWidth: "100%",
    marginVertical: 8,
    padding: 0,
    borderWidth: 0,
    backgroundColor: "transparent",
    alignSelf: "center",
    cursor: "pointer",
    ...({ outlineStyle: "none" } as unknown as ViewStyle),
  } as ViewStyle,
  uaePassImageButtonFocused: {
    outlineWidth: 2,
    outlineStyle: "solid",
    outlineColor: UI_PRIMITIVES.uaePassBlack,
    outlineOffset: 2,
    borderRadius: 8,
  } as ViewStyle,
  uaePassImageButtonPressed: {
    opacity: 0.8,
  },
  uaePassImageButtonDisabled: {
    opacity: 0.5,
  },
  uaePassImagePlaceholder: {
    width: UAE_PASS_BUTTON_WIDTH,
    maxWidth: "100%",
    marginVertical: 8,
    alignSelf: "center",
  },
  dividerContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    marginVertical: 12,
  },
  dividerLine: {
    flex: 1,
    height: 2,
    borderRadius: 1,
  },
  orText: {
    textAlign: "center",
  },
  errorText: {
    textAlign: "center",
    marginBottom: 8,
    lineHeight: 24,
  },
  errorTextCompact: {
    marginBottom: 0,
  },
  forgotPasswordNoErrorCompact: {
    marginTop: -28,
    marginBottom: 24,
  },
  forgotPasswordWithErrorCompact: {
    marginTop: -4,
    marginBottom: 0,
  },
  errorSlotCompact: {
    height: 25,
    justifyContent: "flex-start",
  },
  loaderOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: UI_PRIMITIVES.overlayBackdrop,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  loaderContainer: {
    width: 120,
    height: 120,
    justifyContent: "center",
    alignItems: "center",
  },
  loaderImage: {
    width: "100%",
    height: "100%",
  },
});

const EMIRATES_ID_PREFIX = '784';
const EMIRATES_ID_PREFIX_FORMATTED = '784 - ';

let emiratesIdMeasureCanvas: HTMLCanvasElement | null = null;

/** Width of the Arabic Emirates ID text, in the same frame as the value so the field does not scroll. */
function measureEmiratesIdInputWidth(text: string): number {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return text.length * 9;
  }
  emiratesIdMeasureCanvas ??= document.createElement('canvas');
  const context = emiratesIdMeasureCanvas.getContext('2d');
  if (!context) {
    return text.length * 9;
  }
  context.font = '400 16px Matter';
  // Extra room for the caret so the browser does not scroll the previous digits left.
  return Math.ceil(context.measureText(text).width) + 10;
}

function applyEmiratesIdTextChange(text: string, currentEmiratesId: string): string {
  let input = text;
  const digitsOnly = input.replace(/\D/g, '');
  if (digitsOnly.startsWith('784')) {
    input = digitsOnly.substring(3);
  }

  const rawChars = input.replace(/[^0-9*∗]/g, '');
  let finalDigits = '';
  const oldUserDigits =
    currentEmiratesId.length > 3 ? currentEmiratesId.substring(3) : '';

  let oldAsteriskCount = 0;
  for (let i = 0; i < rawChars.length; i++) {
    const char = rawChars.charAt(i);
    if (char === '*' || char === '∗') {
      const digitIndex = 8 + oldAsteriskCount;
      if (digitIndex < oldUserDigits.length) {
        finalDigits += oldUserDigits.charAt(digitIndex);
      }
      oldAsteriskCount++;
    } else if (/\d/.test(char)) {
      finalDigits += char;
    }
  }

  if (finalDigits.length > 12) {
    finalDigits = finalDigits.substring(0, 12);
  }

  if (finalDigits.length === 0) {
    return '';
  }
  return `784${finalDigits}`;
}

function formatEmiratesIdDisplayValue(emiratesId: string): string {
  if (emiratesId.length <= 3) {
    return '';
  }
  const userDigits = emiratesId.substring(3);
  let formatted = '';
  if (userDigits.length > 0) {
    formatted += userDigits.substring(0, 4);
  }
  if (userDigits.length > 4) {
    formatted += ` - ${userDigits.substring(4, 8)}`;
  }
  if (userDigits.length > 8) {
    const lastChunk = userDigits.substring(8, 12);
    formatted += ` - ${'∗'.repeat(lastChunk.length)}`;
  }
  return formatted;
}

const EmiratesIdInputStep = ({
  emiratesId,
  setEmiratesId,
  onContinue,
  isSmallViewport,
  error,
}: {
  emiratesId: string;
  setEmiratesId: (val: string) => void;
  onContinue: () => void;
  isSmallViewport: boolean;
  error?: string;
}) => {
  const { t } = useTranslation();
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const emiratesIdInputRef = useRef<any>(null);

  const handleTextChange = (text: string) => {
    setEmiratesId(applyEmiratesIdTextChange(text, emiratesId));
  };

  const isValid = emiratesId.length === 15;
  const errorMsg =
    error ||
    (emiratesId.length > 0 && !isValid ? t('auth.invalidEmiratesId') : '');

  const displayValue = formatEmiratesIdDisplayValue(emiratesId);

  const emiratesIdPlaceholder = t("auth.emiratesIdInputPlaceholder") || "xxxx - xxxx - xxxx";
  const rtlInputWidth = isRTL
    ? measureEmiratesIdInputWidth(displayValue || emiratesIdPlaceholder)
    : 0;

  useLayoutEffect(() => {
    if (!isRTL) return;
    const node = emiratesIdInputRef.current;
    if (node && typeof node.scrollLeft === 'number') {
      node.scrollLeft = 0;
    }
  }, [displayValue, isRTL, rtlInputWidth]);
  const emiratesIdPrefix = (
    <View style={{ marginRight: -6, justifyContent: 'center' }}>
      <Typography style={{ fontSize: 16, color: colors.fontPrimary, marginTop: 0 }}>
        {EMIRATES_ID_PREFIX_FORMATTED}
      </Typography>
    </View>
  );

  return (
    <View
      style={[
        styles.column,
        compactStyle(isSmallViewport, styles.columnCompact),
        { alignItems: 'center' }
      ]}
    >
      <Typography
        variant={isSmallViewport ? "title2" : "title1"}
        color="primary"
        style={styles.columnTitle}
      >
        {t("auth.login")}
      </Typography>

      <View
        style={[
          styles.formSection,
          compactStyle(isSmallViewport, styles.formSectionCompact),
          { width: '100%', marginTop: 24 }
        ]}
      >
        <View
          style={[
            styles.inputGroup,
            compactStyle(isSmallViewport, styles.inputGroupCompact),
            { minHeight: 100 } // Reserves space for the error message to prevent modal resize
          ]}
        >
          <TextInput
            ref={emiratesIdInputRef}
            label={t("auth.emiratesId")}
            placeholder={emiratesIdPlaceholder}
            value={displayValue}
            onChangeText={handleTextChange}
            onSubmitEditing={isValid ? onContinue : undefined}
            // Arabic: TextInput renders rightIcon first, so the prefix stays before the digits.
            leftIcon={isRTL ? undefined : emiratesIdPrefix}
            rightIcon={isRTL ? emiratesIdPrefix : undefined}
            keyboardType="numeric"
            error={errorMsg}
            selectTextOnFocus={false}
            inputContainerStyle={{
              borderWidth: 0,
              borderColor: 'transparent',
              outlineStyle: 'none',
              paddingLeft: 16,
              paddingRight: 16,
              ...(isRTL
                ? { flexDirection: 'row', justifyContent: 'flex-end', direction: 'ltr' }
                : {}),
            } as any}
            inputStyle={{
              outlineStyle: 'none',
              ...(isRTL
                ? {
                    // Hug the text so "784 - " sits right next to the digits on the right.
                    // RN Web turns `flex` into flexBasis 0%, which would collapse the input to zero width.
                    flex: undefined,
                    flexGrow: 0,
                    flexShrink: 1,
                    flexBasis: 'auto',
                    minWidth: 0,
                    width: rtlInputWidth,
                    overflow: 'hidden',
                    textAlign: 'left',
                    writingDirection: 'ltr',
                    direction: 'ltr',
                  }
                : {}),
            } as any}
          />
        </View>

        <Button
          title={t("auth.continue")}
          variant="primary"
          onPress={onContinue}
          fullWidth
          style={[
            styles.button,
            compactStyle(isSmallViewport, styles.compactButton),
          ]}
          disabled={!isValid}
        />
      </View>
    </View>
  );
};

function maskLoginDialogContactInfo(
  contactInfo: string,
  type: "mobile" | "email",
): string {
  if (!contactInfo || contactInfo === "Phone" || contactInfo === "Email") {
    return contactInfo;
  }
  if (type === "mobile") {
    return contactInfo;
  }
  if (contactInfo.length < 6) {
    return contactInfo;
  }
  return (
    contactInfo.substring(0, 2) +
    "*".repeat(Math.max(0, contactInfo.length - 6)) +
    contactInfo.substring(contactInfo.length - 4)
  );
}

interface LoginDialogOtpResendPromptProps {
  resendTimer: number;
  onResend?: () => void;
  isRTL: boolean;
  resendLineHeight: number;
  fontPrimary: string;
  fontTertiary: string;
  didntReceiveCodeLabel: string;
  resendOtpLabel: string;
  resendTimerBefore: string;
  resendTimerAfter: string;
  resendSuffixNeedsLeadingSpace: boolean;
  resendCountdown: string;
}

function LoginDialogOtpResendPrompt({
  resendTimer,
  onResend,
  isRTL,
  resendLineHeight,
  fontPrimary,
  fontTertiary,
  didntReceiveCodeLabel,
  resendOtpLabel,
  resendTimerBefore,
  resendTimerAfter,
  resendSuffixNeedsLeadingSpace,
  resendCountdown,
}: Readonly<LoginDialogOtpResendPromptProps>) {
  const resendSemiBoldBase = {
    fontFamily: "Matter-SemiBold" as const,
    fontWeight: "600" as const,
    fontSize: 14,
    lineHeight: resendLineHeight,
  };

  let resendAction: React.ReactNode;
  if (resendTimer > 0) {
    resendAction = (
      <Typography style={{ ...resendSemiBoldBase, color: fontTertiary }}>
        {resendTimerBefore}
        {isRTL && !resendTimerBefore.endsWith(" ") ? "\u00a0" : null}
        <Typography
          style={{
            ...resendSemiBoldBase,
            color: fontPrimary,
            ...(isRTL
              ? {
                  marginHorizontal: resendSuffixNeedsLeadingSpace ? 8 : 4,
                  writingDirection: "ltr",
                  direction: "ltr",
                  unicodeBidi: "isolate",
                }
              : {}),
          } as any}
        >
          {resendCountdown}
        </Typography>
        {resendTimerAfter}
      </Typography>
    );
  } else {
    resendAction = (
      <Typography
        style={{ ...resendSemiBoldBase, color: fontTertiary }}
        onPress={onResend}
      >
        {resendOtpLabel}
      </Typography>
    );
  }

  return (
    <Typography
      style={{
        fontFamily: "Matter-Regular",
        fontWeight: "400",
        fontSize: 14,
        lineHeight: resendLineHeight,
        textAlign: "center",
        color: fontTertiary,
      }}
    >
      {didntReceiveCodeLabel}{" "}
      {resendAction}
    </Typography>
  );
}

const OTPVerificationStep = ({
  contactInfo,
  otpValue,
  setOtpValue,
  resendTimer,
  onVerify,
  onResend,
  isSmallViewport,
  type,
  onClose,
  onSupportTicketPress,
  error,
  isLoading,
}: {
  contactInfo: string;
  otpValue: string;
  setOtpValue: (val: string) => void;
  resendTimer: number;
  onVerify: () => void;
  onResend?: () => void;
  isSmallViewport: boolean;
  type: "mobile" | "email";
  onClose: () => void;
  onSupportTicketPress: () => void;
  error?: string;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  const {
    before: resendTimerBefore,
    after: resendTimerAfter,
    suffixNeedsLeadingSpace: resendSuffixNeedsLeadingSpace,
  } = splitOtpResendTimerTemplate(t("otp.resendOTPTimer"));
  const resendCountdown = formatOtpResendCountdown(resendTimer);
  const resendLineHeight = isRTL ? 22 : 14;
  const verificationSentLabel =
    type === "mobile"
      ? t("auth.mobileVerificationSent")
      : t("auth.emailVerificationSent");

  return (
    <View
      style={[
        styles.column,
        compactStyle(isSmallViewport, styles.columnCompact),
        { alignItems: isRTL ? 'flex-end' : 'flex-start', width: '100%', paddingHorizontal: isSmallViewport ? 16 : 0, gap: 12 }
      ]}
    >
      <Typography
        style={{
          fontFamily: 'Matter-SemiBold',
          fontWeight: '600',
          fontSize: 19,
          lineHeight: 19,
          textAlign: isRTL ? 'right' : 'left',
          writingDirection: isRTL ? 'rtl' : 'ltr',
          width: '100%',
          marginBottom: 0,
          marginTop: 12,
          color: colors.fontPrimary
        }}
      >
        {t("auth.verifyIdentity")}
      </Typography>

      <Typography
        style={{
          fontFamily: 'Matter-Regular',
          fontWeight: '400',
          fontSize: 14,
          lineHeight: 22,
          textAlign: isRTL ? 'right' : 'left',
          writingDirection: isRTL ? 'rtl' : 'ltr',
          width: '100%',
          marginBottom: 24,
          color: colors.fontTertiary
        }}
      >
        {verificationSentLabel}{" "}
        <Typography style={{ fontFamily: 'Matter-SemiBold', fontWeight: '600', fontSize: 14, lineHeight: 22, color: colors.fontPrimary, writingDirection: 'ltr', direction: 'ltr', unicodeBidi: 'isolate' } as any}>
          {`\u2066${maskLoginDialogContactInfo(contactInfo, type)}\u2069`}
        </Typography>
      </Typography>

      <View style={{ width: '100%', alignItems: 'center', marginBottom: 12 }}>
        <OTPInput
          length={4}
          value={otpValue}
          onChangeText={setOtpValue}
          onEnterSubmit={onVerify}
          loading={isLoading}
        />
        {error ? (
          <Typography variant="subhead" color="error" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        ) : null}
      </View>

      <View style={{ width: '100%', alignItems: 'center', gap: 24, marginBottom: 16 }}>
        <LoginDialogOtpResendPrompt
          resendTimer={resendTimer}
          onResend={onResend}
          isRTL={isRTL}
          resendLineHeight={resendLineHeight}
          fontPrimary={colors.fontPrimary}
          fontTertiary={colors.fontTertiary}
          didntReceiveCodeLabel={t("auth.didntReceiveCode")}
          resendOtpLabel={t("otp.resendOTP")}
          resendTimerBefore={resendTimerBefore}
          resendTimerAfter={resendTimerAfter}
          resendSuffixNeedsLeadingSpace={resendSuffixNeedsLeadingSpace}
          resendCountdown={resendCountdown}
        />

        <Typography style={{ fontFamily: 'Matter-Regular', fontWeight: '400', fontSize: 14, lineHeight: 14, textAlign: 'center', color: colors.fontTertiary }}>
          {t("auth.noAccessToPhone")}{" "}
          <Typography
            style={{ fontFamily: 'Matter-SemiBold', fontWeight: '600', fontSize: 14, lineHeight: 14, color: colors.fontErrorText, cursor: 'pointer' }}
            onPress={onSupportTicketPress}
          >
            {t("auth.raiseSupportTicket")}
          </Typography>
        </Typography>
      </View>

      <Button
        title={isRTL ? t("auth.verifyAndContinue") : t("auth.verify")}
        variant="primary"
        onPress={onVerify}
        fullWidth
        style={[
          styles.button,
          compactStyle(isSmallViewport, styles.compactButton),
        ]}
        disabled={otpValue.length !== 4 || isLoading}
      />
    </View>
  );
};

export default LoginDialog;
